import Highcharts from "highcharts/highstock";

const XAXIS_KEY = "time";
const BAR_KEY = "consumed";
const LINE_KEY = "optimization";
const BAR_FILL = "#413ea0";
const LINE_STROKE = "#1c9c29";

// Helper: find closest point in a series to the left of given x
const findClosestPoint = (series, targetX) => {
  if (!series || !series.data || !series.data.length) return null;
  let closest = null;
  let minDiff = Infinity;
  for (let i = 0; i < series.data.length; i++) {
    const p = series.data[i];
    // Only consider points to the left (p.x <= targetX)
    if (p.x <= targetX) {
      const diff = targetX - p.x; // Distance to the left
      if (diff < minDiff) {
        minDiff = diff;
        closest = p;
      }
    }
  }
  return closest;
};

// Helper: find left index (index of the most recent point with x <= targetX)
const findLeftIndex = (series, targetX) => {
  if (!series || !series.data || !series.data.length) return -1;
  let leftIdx = -1;
  for (let i = 0; i < series.data.length; i++) {
    if (series.data[i].x <= targetX) leftIdx = i;
    else break;
  }
  return leftIdx;
};

// Helper: safely extract raw array [ts, y, metrics] from a point if it exists
const getRawFromPoint = (point, series) => {
  if (!point) return null;
  if (Array.isArray(point.options) && point.options.length >= 3) return point.options;
  if (Array.isArray(point.userOptions) && point.userOptions.length >= 3) return point.userOptions;
  if (series && Array.isArray(series.options?.data) && typeof point.index === "number") {
    const raw = series.options.data[point.index];
    if (Array.isArray(raw) && raw.length >= 3) return raw;
  }
  return null;
};

// Tooltip formatter
const createTooltipFormatter = () => {
  return function () {
    const x = this.x;
    const chart = this.points && this.points.length ? this.points[0].series.chart : this.series.chart;

    const header = `<b>${Highcharts.dateFormat("%e %b %Y %H:%M:%S", x)}</b><br/>`;

    let siteProduction = 0;
    let chargeLevel = 0;
    let batteryCapacityUsed = 0;
    let siteConsumption = 0;

    // Collect ALL setpoint series
    const setpointSeriesList = chart.series.filter(s => s.name === "Green Charging" || s.name === "AI optimization");

    // ✅ Aggregate metrics across all setpoint series
    setpointSeriesList.forEach(series => {
      if (!Array.isArray(series.data) || !series.data.length) return;

      const firstX = series.data[0].x;
      const lastX = series.data[series.data.length - 1].x;
      if (x < firstX || x > lastX) return;

      const leftIndex = findLeftIndex(series, x);
      if (leftIndex < 0) return;

      const leftPoint = series.data[leftIndex];
      const raw = getRawFromPoint(leftPoint, series);
      const metrics = raw?.[2];
      if (!metrics) return;

      siteProduction += metrics.siteProduction ?? 0;
      chargeLevel += metrics.chargeLevel ?? 0;
      batteryCapacityUsed += metrics.batteryCapacityUsed ?? 0;
      siteConsumption += metrics.siteConsumption ?? 0;
    });

    const metaBlock = `
      Site Production: <b>${siteProduction} W</b><br/>
      Charge Level: <b>${chargeLevel} A</b><br/>
      Battery Capacity Used: <b>${batteryCapacityUsed} W</b><br/>
      Net Home Consumption: <b>${siteConsumption} W</b><br/>
    `;

    const body = chart.series
      .filter(s => !s.options.isInternal && s.visible !== false)
      .map(s => {
        const isSetpoint = s.name === "Green Charging" || s.name === "AI optimization";
        const seriesColor = s.color || s.options?.color || "#333";

        if (isSetpoint) {
          let displayValue = "0 W";
          const leftIndex = findLeftIndex(s, x);
          const leftPoint = leftIndex >= 0 ? s.data[leftIndex] : null;
          if (leftPoint?.y != null) displayValue = `${leftPoint.y} W`;
          return `<span style="color:${seriesColor}">${s.name}</span>: <b style="color:${seriesColor}">${displayValue}</b><br/>`;
        }

        const closest = findClosestPoint(s, x);
        const value = closest?.y != null ? `${closest.y} W` : "0 W";
        return `<span style="color:${seriesColor}">${s.name}</span>: <b style="color:${seriesColor}">${value}</b><br/>`;
      })
      .join("");

    return header + metaBlock + body;
  };
};

// Chart options
const createChartOptions = (data, energyConsumptionData) => ({
  chart: {
    type: "line",
    height: null, // Let Highcharts calculate height based on container
    marginRight: 20,
    zoomType: "x",
    panning: {
      enabled: true,
    },
    panKey: "shift",
    resetZoomButton: {
      position: {
        x: -50,
        y: -32,
      },
      theme: {
        padding: 4,
        r: 4,
      },
    },
  },
  title: {
    text: '<span style="color:white">.</span>',
    align: "left",
    style: { color: "#333", fontWeight: "bold", fontSize: "16px" },
  },
  xAxis: {
    type: "datetime",
    ordinal: false,
    crosshair: true,
    title: { text: "Time" },
    labels: { style: { color: "#666" } },
  },
  yAxis: {
    title: { text: "Power (W)" },
    gridLineColor: "#eee",
    gridLineDashStyle: "Dash",
    labels: {
      style: { color: "#666" },
      formatter: function () {
        return `${this.value}`;
      },
    },
    opposite: false,
    plotLines: [],
  },
  tooltip: {
    shared: true,
    borderColor: "#ccc",
    backgroundColor: "#fff",
    useHTML: true,
    formatter: createTooltipFormatter(),
  },
  legend: { align: "center", verticalAlign: "bottom" },
  plotOptions: {
    series: {
      animation: false,
      states: { hover: { enabled: true, brightness: 0.05 } },
    },
    area: { marker: { radius: 2, enabled: false }, lineWidth: 2 },
    line: { marker: { enabled: false }, lineWidth: 2 },
  },
  series: [
    {
      name: "Green Charging",
      type: "line",
      data: data?.greenChargingSetpointPower || [],
      color: "#00c853",
      lineWidth: 2,
      marker: { enabled: false },
      step: "left",
    },
    {
      name: "AI optimization",
      type: "line",
      data: data?.smartChargingSetpointPower || [],
      color: "#87CEEB",
      lineWidth: 2,
      marker: { enabled: false },
      step: "left",
    },
    {
      name: "Power Consumed",
      type: "line",
      data: energyConsumptionData || [],
      color: "#FFA500",
      lineWidth: 1,
      marker: { enabled: false },
    },
  ],
  credits: { enabled: false },
  exporting: {
    enabled: true,
    allowHTML: true,
    buttons: {
      contextButton: {
        enabled: true,
        menuItems: ["viewFullscreen", "separator", "downloadCSV", "downloadXLS", "downloadPNG"],
      },
    },
  },
  time: {
    useUTC: false,
  },
});

const CHART_TITLE = "Schedules and Energy Consumption";

export { XAXIS_KEY, BAR_KEY, LINE_KEY, BAR_FILL, LINE_STROKE, createChartOptions, CHART_TITLE };
